import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLoraModelDetail } from '../../api/queries/lora';

interface LoraModelDetailDrawerProps {
  entityId: string;
  loraId: string | null;
  onClose: () => void;
  onTest?: (loraId: string, triggerWord: string) => void;
}

export function LoraModelDetailDrawer({
  entityId,
  loraId,
  onClose,
  onTest,
}: LoraModelDetailDrawerProps) {
  const detail = useLoraModelDetail(entityId, loraId ?? undefined);
  const model = detail.data;
  const canTest =
    model?.trainingStatus === 'complete' ||
    model?.trainingStatus === 'approved';

  return (
    <Drawer
      anchor="right"
      open={loraId !== null}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 480 } } }}
    >
      {detail.isLoading && (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {model && (
        <Stack spacing={2} sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{model.name}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={model.trainingStatus} size="small" />
            {model.version && (
              <Chip label={model.version} size="small" variant="outlined" />
            )}
            {model.trainer && (
              <Chip label={model.trainer} size="small" variant="outlined" />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {model.triggerWord}
            </Typography>
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(model.triggerWord)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Stack>

          {canTest && onTest && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onTest(model.id, model.triggerWord)}
            >
              Test Model
            </Button>
          )}

          {model.trainingMetrics && (
            <>
              <Divider />
              <Typography variant="subtitle2">Training Metrics</Typography>
              <Stack spacing={0.5}>
                {model.trainingMetrics.final_loss != null && (
                  <MetricRow
                    label="Final Loss"
                    value={model.trainingMetrics.final_loss.toFixed(4)}
                  />
                )}
                {model.trainingMetrics.steps_completed != null && (
                  <MetricRow
                    label="Steps"
                    value={String(model.trainingMetrics.steps_completed)}
                  />
                )}
                {model.trainingMetrics.wall_clock_seconds != null && (
                  <MetricRow
                    label="Training Time"
                    value={`${Math.round(model.trainingMetrics.wall_clock_seconds / 60)} min`}
                  />
                )}
                {model.trainingMetrics.peak_vram_bytes != null && (
                  <MetricRow
                    label="Peak VRAM"
                    value={`${(model.trainingMetrics.peak_vram_bytes / 1e9).toFixed(1)} GB`}
                  />
                )}
              </Stack>
            </>
          )}

          {model.sampleImageUrls.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">Sample Images</Typography>
              <ImageList cols={3} gap={8}>
                {model.sampleImageUrls.map((url, i) => (
                  <ImageListItem key={url}>
                    <img
                      src={url}
                      alt={`Sample ${i + 1}`}
                      loading="lazy"
                      style={{ borderRadius: 4 }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </>
          )}

          {model.lastError && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="error">
                Error
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
              >
                {model.lastError}
              </Typography>
            </>
          )}

          {model.datasetUrl && (
            <>
              <Divider />
              <Button
                startIcon={<DownloadIcon />}
                href={model.datasetUrl}
                target="_blank"
                rel="noopener"
                variant="outlined"
                size="small"
              >
                Download Training Dataset
              </Button>
            </>
          )}

          {model.hyperparameters && (
            <>
              <Divider />
              <Typography variant="subtitle2">Hyperparameters</Typography>
              <Box
                component="pre"
                sx={{
                  fontSize: 12,
                  bgcolor: 'grey.100',
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  m: 0,
                }}
              >
                {JSON.stringify(model.hyperparameters, null, 2)}
              </Box>
            </>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Stack>
  );
}
